import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { getJsClientTemplateFor, getJsClientTemplateNameFor } from './js-client-template';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, skip, switchMap } from 'rxjs/operators';
import { JsClientApiService, JsClientConfigDto, ConfigProviderDto, PurgeResultDto } from '../../services/js-client-api.service';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { ProvidersApiService } from '../../services/providers-api.service';
import { TrackingScriptsFormComponent } from './form/tracking-scripts-form.component';

@Component({
  selector: 'app-tracking-scripts',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, TrackingScriptsFormComponent],
  templateUrl: './tracking-scripts.component.html',
  styleUrls: ['./tracking-scripts.component.scss']
})
export class TrackingScriptsComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  @ViewChild(TrackingScriptsFormComponent) trackingScriptsFormComponent?: TrackingScriptsFormComponent;

  configs: JsClientConfigDto[] = [];
  filteredConfigs: JsClientConfigDto[] = [];

  isLoading = false;
  loadingError: string | null = null;
  searchTerm = '';
  pageSizeOptions = [10, 25, 50];
  pageSize = 10;
  currentPage = 0;
  panelOpen = false;
  formMode: 'create' | 'edit' = 'create';
  isDeploying = new Set<number>();
  isPurging = new Set<number>();

  activeActionMenuId: number | null = null;
  activeActionItem: JsClientConfigDto | null = null;
  actionMenuPosition = { top: 0, left: 0 };
  actionMenuArrowOffset = 24;
  providerNameMap: Record<string, string> = {};

  snippetModal: { providerName: string; providerID: string; script: string; gcsUrl: string | null; templateName: string } | null = null;
  snippetCopied = false;
  private snippetCopyTimer: ReturnType<typeof setTimeout> | null = null;

  readonly locale = 'it-IT';

  constructor(
    private jsClientApi: JsClientApiService,
    private authService: AuthService,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private providersApi: ProvidersApiService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.authService.selectedDcr$
        .pipe(distinctUntilChanged(), skip(1), switchMap(() => this.authService.waitForEntitySync()))
        .subscribe(() => {
          this.closePanel();
          this.loadConfigs();
        })
    );
    this.authService.waitForEntitySync().subscribe(() => this.loadConfigs());
    this.providersApi.getProviders().subscribe({
      next: providers => {
        this.providerNameMap = Object.fromEntries(providers.map(p => [p.ID, p.Name]));
      },
      error: () => {}
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    const target = event.target instanceof HTMLElement ? event.target : null;
    const clickedMenu = !!target?.closest('.tracking-scripts__action-menu');
    const clickedTrigger = !!target?.closest('.tracking-scripts__action-trigger');
    if (!clickedMenu && !clickedTrigger) {
      this.closeActionMenu();
    }
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (this.snippetModal) { this.closeSnippetModal(); return; }
    this.closeActionMenu();
  }

  loadConfigs(): void {
    this.isLoading = true;
    this.loadingError = null;
    const sub = this.jsClientApi.getConfigs().subscribe({
      next: (list) => {
        this.configs = list;
        this.resetPagination();
        this.applyFilters();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('[tracking-scripts] load failed', error);
        this.loadingError = error?.message || this.translate.instant('trackingScripts.errors.load');
        this.configs = [];
        this.filteredConfigs = [];
        this.isLoading = false;
      }
    });
    this.subscriptions.add(sub);
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    const activeDcrId = this.authService.getActiveDcrId();
    this.filteredConfigs = this.configs.filter(config => {
      const matchesTerm = !term || config.Name.toLowerCase().includes(term);
      const matchesEntity = !activeDcrId || config.PublisherID === activeDcrId;
      return matchesTerm && matchesEntity;
    });
    this.ensureValidPage();
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    this.resetPagination();
    this.applyFilters();
  }

  clearSearch(): void {
    if (!this.searchTerm) {
      return;
    }
    this.searchTerm = '';
    this.resetPagination();
    this.applyFilters();
  }

  get paginatedConfigs(): JsClientConfigDto[] {
    const startIndex = this.currentPage * this.pageSize;
    return this.filteredConfigs.slice(startIndex, startIndex + this.pageSize);
  }

  get totalPages(): number {
    if (!this.filteredConfigs.length) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.filteredConfigs.length / this.pageSize));
  }

  changePageSize(size: Event | string | number): void {
    let numeric: number;
    if (size instanceof Event) {
      const target = size.target as HTMLSelectElement | null;
      numeric = target ? parseInt(target.value, 10) : this.pageSize;
    } else if (typeof size === 'string') {
      numeric = parseInt(size, 10);
    } else {
      numeric = size;
    }

    if (!Number.isFinite(numeric) || numeric <= 0 || numeric === this.pageSize) {
      return;
    }
    this.pageSize = numeric;
    this.resetPagination();
    this.applyFilters();
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages) {
      return;
    }
    this.currentPage = page;
  }

  openCreatePanel(): void {
    this.closeActionMenu();
    this.formMode = 'create';
    this.panelOpen = true;
    setTimeout(() => this.trackingScriptsFormComponent?.openCreate(this.getUsedProviderIds()));
  }

  openEditPanel(item: JsClientConfigDto): void {
    this.closeActionMenu();
    this.formMode = 'edit';
    this.panelOpen = true;
    setTimeout(() => this.trackingScriptsFormComponent?.openEdit(item, this.getUsedProviderIds(item.ID)));
  }

  private getUsedProviderIds(excludeConfigId?: number): string[] {
    const used = new Set<string>();
    for (const config of this.configs) {
      if (excludeConfigId && config.ID === excludeConfigId) continue;
      for (const provider of (config.Providers || [])) {
        used.add(provider.ProviderID);
      }
    }
    return Array.from(used);
  }

  closePanel(): void {
    this.panelOpen = false;
  }

  handleFormSaved(): void {
    this.panelOpen = false;
    this.loadConfigs();
  }

  async deleteConfig(item: JsClientConfigDto): Promise<void> {
    if (!confirm(this.translate.instant('trackingScripts.actions.deleteConfirm', { name: item.Name }))) {
      return;
    }
    const deployedProviders = (item.Providers || []).filter(p => p.DeployStatus === 'deployed');
    if (deployedProviders.length > 0) {
      const fileList = deployedProviders.map(p => {
        const name = this.providerNameMap[p.ProviderID] || p.ProviderID;
        return `  • ${name}  (${p.ProviderID}/config.js)`;
      }).join('\n');
      if (!confirm(this.translate.instant('trackingScripts.actions.deleteConfirmGcs', { files: fileList }))) {
        return;
      }
    }
    try {
      await firstValueFrom(this.jsClientApi.deleteConfig(item.ID));
      this.notificationService.notify('success', this.translate.instant('trackingScripts.messages.deleted'));
      this.loadConfigs();
    } catch (error) {
      console.error('[tracking-scripts] delete failed', error);
      this.notificationService.notify('error', (error as Error)?.message || this.translate.instant('trackingScripts.errors.delete'));
    }
    this.closeActionMenu();
  }

  async toggleStatus(item: JsClientConfigDto): Promise<void> {
    const newStatus = item.Status === 1 ? 0 : 1;
    const confirmKey = newStatus === 0
      ? 'trackingScripts.actions.deactivateConfirm'
      : 'trackingScripts.actions.activateConfirm';
    if (!confirm(this.translate.instant(confirmKey, { name: item.Name }))) {
      return;
    }
    this.closeActionMenu();
    this.isDeploying.add(item.ID);
    try {
      await firstValueFrom(this.jsClientApi.updateConfig({ ID: item.ID, Status: newStatus }));
      const result = await firstValueFrom(this.jsClientApi.deploy(item.ID));
      if (result.errors && result.errors.length > 0) {
        this.notificationService.notify(
          'warning',
          this.translate.instant('trackingScripts.deploy.partialError', { errors: result.errors.length })
        );
      } else {
        const successKey = newStatus === 0
          ? 'trackingScripts.messages.statusDeactivated'
          : 'trackingScripts.messages.statusUpdated';
        this.notificationService.notify('success', this.translate.instant(successKey));
      }
      this.loadConfigs();
    } catch (error) {
      console.error('[tracking-scripts] toggle status failed', error);
      this.notificationService.notify('error', this.translate.instant('trackingScripts.errors.statusUpdate'));
    } finally {
      this.isDeploying.delete(item.ID);
    }
  }

  async deployConfig(item: JsClientConfigDto): Promise<void> {
    this.isDeploying.add(item.ID);
    this.closeActionMenu();
    try {
      const result = await firstValueFrom(this.jsClientApi.deploy(item.ID));
      if (result.errors && result.errors.length > 0) {
        this.notificationService.notify(
          'warning',
          this.translate.instant('trackingScripts.deploy.partialError', { errors: result.errors.length })
        );
      } else {
        this.notificationService.notify(
          'success',
          this.translate.instant('trackingScripts.deploy.success', { count: result.deployed.length })
        );
      }
      this.loadConfigs();
    } catch (error) {
      console.error('[tracking-scripts] deploy failed', error);
      this.notificationService.notify('error', this.translate.instant('trackingScripts.deploy.error'));
    } finally {
      this.isDeploying.delete(item.ID);
    }
  }

  async purgeCdn(item: JsClientConfigDto): Promise<void> {
    this.isPurging.add(item.ID);
    this.closeActionMenu();
    try {
      const result: PurgeResultDto = await firstValueFrom(this.jsClientApi.purge(item.ID));
      if (result.skipped) {
        this.notificationService.notify('info', this.translate.instant('trackingScripts.purge.skipped'));
      } else if (result.errors && result.errors.length > 0) {
        this.notificationService.notify(
          'warning',
          this.translate.instant('trackingScripts.purge.partialError', { errors: result.errors.length })
        );
      } else {
        this.notificationService.notify(
          'success',
          this.translate.instant('trackingScripts.purge.success', { count: result.invalidated.length })
        );
      }
    } catch (error) {
      console.error('[tracking-scripts] CDN purge failed', error);
      this.notificationService.notify('error', this.translate.instant('trackingScripts.purge.error'));
    } finally {
      this.isPurging.delete(item.ID);
    }
  }

  openSnippetModal(config: JsClientConfigDto, provider: ConfigProviderDto): void {
    const providerName = this.providerNameMap[provider.ProviderID] || provider.ProviderID;
    const autostart = config.Autostart === 1 ? 'true' : 'false';
    const templateName = getJsClientTemplateNameFor(config.Template || 'tnc');
    let script = getJsClientTemplateFor(config.Template || 'tnc')
      .replace(/\{\{PROVIDER_ID\}\}/g, provider.ProviderID)
      .replace(/\{\{PROVIDER_NAME\}\}/g, providerName)
      .replace(/\{\{AUTOSTART\}\}/g, autostart);
    if (config.SnippetAppend?.trim()) {
      script += '\n\n' + config.SnippetAppend.trim();
    }
    this.snippetModal = { providerName, providerID: provider.ProviderID, script, gcsUrl: provider.GcsUrl || null, templateName };
  }

  closeSnippetModal(): void {
    this.snippetModal = null;
    this.snippetCopied = false;
    if (this.snippetCopyTimer) { clearTimeout(this.snippetCopyTimer); this.snippetCopyTimer = null; }
  }

  copySnippet(): void {
    if (!this.snippetModal) return;
    navigator.clipboard.writeText(this.snippetModal.script).then(() => {
      this.snippetCopied = true;
      this.notificationService.notify('success', this.translate.instant('trackingScripts.snippet.copied'));
      if (this.snippetCopyTimer) clearTimeout(this.snippetCopyTimer);
      this.snippetCopyTimer = setTimeout(() => { this.snippetCopied = false; }, 2000);
    });
  }

  downloadSnippet(): void {
    if (!this.snippetModal) return;
    const templateName = this.snippetModal.templateName;
    const safeName = this.snippetModal.providerName.replace(/[^a-zA-Z0-9_\-]/g, '_');
    const filename = `${templateName}_tracking_${safeName}.js.txt`;
    const blob = new Blob([this.snippetModal.script], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  getProviderName(providerId: string): string {
    return this.providerNameMap[providerId] || providerId;
  }

  getDeployStatusLabel(status: string): string {
    if (status === 'deployed') {
      return this.translate.instant('trackingScripts.deploy.statusDeployed');
    }
    if (status === 'error') {
      return this.translate.instant('trackingScripts.deploy.statusError');
    }
    return this.translate.instant('trackingScripts.deploy.statusPending');
  }

  getDeployStatusClass(status: string): string {
    if (status === 'deployed') {
      return 'is-deployed';
    }
    if (status === 'error') {
      return 'is-error';
    }
    return 'is-pending';
  }

  getScriptsLabel(config: JsClientConfigDto): string {
    if (!config.Scripts || config.Scripts.length === 0) {
      return '—';
    }
    return config.Scripts.join(', ');
  }

  toggleActionsMenu(item: JsClientConfigDto, event: Event): void {
    event.stopPropagation();
    const isOpen = this.activeActionMenuId === item.ID;
    if (isOpen) {
      this.closeActionMenu();
      return;
    }
    this.activeActionMenuId = item.ID;
    this.activeActionItem = item;
    this.updateActionMenuPosition(event.currentTarget as HTMLElement);
  }

  private resetPagination(): void {
    this.currentPage = 0;
  }

  private ensureValidPage(): void {
    if (!this.filteredConfigs.length) {
      this.currentPage = 0;
      return;
    }
    const totalPages = Math.max(1, Math.ceil(this.filteredConfigs.length / this.pageSize));
    const lastPageIndex = Math.max(0, totalPages - 1);
    if (this.currentPage > lastPageIndex) {
      this.currentPage = lastPageIndex;
    }
  }

  private closeActionMenu(): void {
    this.activeActionMenuId = null;
    this.activeActionItem = null;
    this.actionMenuArrowOffset = 24;
  }

  private updateActionMenuPosition(trigger: HTMLElement): void {
    if (typeof window === 'undefined') {
      this.actionMenuPosition = { top: 0, left: 0 };
      this.actionMenuArrowOffset = 24;
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 240;
    const padding = 16;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const preferredLeft = rect.right - menuWidth;
    const maxLeft = viewportWidth - menuWidth - padding;
    const left = Math.min(Math.max(preferredLeft, padding), maxLeft);
    this.actionMenuPosition = {
      top: rect.bottom + 8,
      left
    };

    const buttonCenter = rect.left + rect.width / 2;
    const arrowPadding = 24;
    const relativeCenter = buttonCenter - left;
    const clampedOffset = Math.min(Math.max(relativeCenter, arrowPadding), menuWidth - arrowPadding);
    this.actionMenuArrowOffset = clampedOffset;
  }
}
